import {
  Badge,
  TovButton,
  Input,
  Select,
  SelectOption,
  TovIcon,
  formatEnumLabel,
  msg,
  TovCard,
  useToast,
} from '@tov-plus/ui';
import { CachedDataNotice } from '@tov-plus/pwa';
import { useMemo, useState } from 'react';
import { NavLink } from '@tov-plus/frontend-data';
import { useTovQuery } from '@tov-plus/frontend-data';

import { guestApi } from '../app/api';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import type { GuestVisibleLocation } from '../lib/api/guestApi';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';

type ViewMode = 'list' | 'type' | 'event' | 'map';

const locationsErrorMessage = msg(
  'pages.locations_page.locations_could_not_be_loaded',
);

function getLocationGuidanceLabel(type: GuestVisibleLocation['type']) {
  switch (type) {
    case 'parking':
      return msg('pages.locations_page.parking_and_arrival_guidance');
    case 'transportation_point':
      return msg('pages.locations_page.pickup_shuttle_or_transfer_point');
    case 'accommodation':
      return msg('pages.locations_page.stay_details_and_check_in_planning');
    case 'venue':
    case 'internal_venue_point':
      return msg('pages.locations_page.ceremony_or_event_venue_guidance');
    default:
      return msg('pages.locations_page.occasion_location_guidance');
  }
}

function getContactTypeLabel(contactType: string) {
  switch (contactType) {
    case 'phone':
      return msg('pages.locations_page.call_or_text');
    case 'email':
      return msg('pages.locations_page.email_contact');
    case 'vendor':
      return msg('pages.locations_page.vendor_contact');
    default:
      return msg('pages.locations_page.guest_help_contact');
  }
}

export function LocationsPage() {
  const access = useGuestAccess();
  const notifications = useToast();
  const readyOccasionSlug =
    access.status === 'ready' ? access.experience.occasion.slug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const locationsQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken
        ? guestQueryKeys.locations(readyOccasionSlug, readyGuestToken)
        : guestQueryKeys.locations('pending', 'pending'),
    queryFn: () =>
      guestApi.listVisibleLocations(readyOccasionSlug!, readyGuestToken!),
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
  });
  const locationsResponse = locationsQuery.data ?? null;
  const locations = locationsResponse?.locations ?? [];
  const eventLinks = locationsResponse?.eventLinks ?? [];
  const locationLinks = locationsResponse?.locationLinks ?? [];
  const contacts = locationsResponse?.contacts ?? [];
  const overlays =
    locationsResponse?.overlays.map((overlay) => ({
      id: overlay.id,
      location_id: overlay.location_id,
      label: overlay.label,
      x: overlay.x,
      y: overlay.y,
    })) ?? [];
  const mapMarkers =
    locationsResponse?.mapMarkers.map((item) => ({
      location_id: item.location_id,
      title: item.title,
      type: item.type,
      latitude: item.latitude,
      longitude: item.longitude,
    })) ?? [];
  const [query, setQuery] = useState('');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [eventFilter, setEventFilter] = useState('');

  const selectedLocation = useMemo(
    () =>
      locations.find((location) => location.id === selectedLocationId) ?? null,
    [locations, selectedLocationId],
  );

  const eventIds = useMemo(
    () => Array.from(new Set(eventLinks.map((item) => item.event_id))),
    [eventLinks],
  );
  const eventNameById = useMemo(
    () =>
      new Map(
        access.status === 'ready'
          ? access.experience.events.map((event) => [
              event.id,
              event.displayName,
            ])
          : [],
      ),
    [access],
  );

  const venueMapQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken && selectedLocation?.venueMapAssetId
        ? guestQueryKeys.locations(
            readyOccasionSlug,
            readyGuestToken,
            'venue-map',
            selectedLocation.id,
          )
        : guestQueryKeys.locations('pending', 'pending', 'venue-map'),
    queryFn: () =>
      guestApi.getVisibleVenueMap(
        readyOccasionSlug!,
        readyGuestToken!,
        selectedLocation!.id,
      ),
    enabled: Boolean(
      readyOccasionSlug && readyGuestToken && selectedLocation?.venueMapAssetId,
    ),
  });
  const venueOverlays =
    venueMapQuery.data?.overlays.map((overlay) => ({
      id: overlay.id,
      label: overlay.label,
      x: overlay.x,
      y: overlay.y,
    })) ?? [];

  const filteredLocations = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return locations.filter((location) => {
      if (eventFilter) {
        const linkedEventIds = eventLinks
          .filter((item) => item.location_id === location.id)
          .map((item) => item.event_id);
        if (!linkedEventIds.includes(eventFilter)) {
          return false;
        }
      }

      if (!normalizedQuery) {
        return true;
      }

      return [
        location.title,
        location.type,
        location.description ?? '',
        location.addressText ?? '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(normalizedQuery);
    });
  }, [eventFilter, eventLinks, locations, query]);

  const groupedByType = useMemo(() => {
    const groups = new Map<string, GuestVisibleLocation[]>();
    for (const location of filteredLocations) {
      const current = groups.get(location.type) ?? [];
      current.push(location);
      groups.set(location.type, current);
    }
    return Array.from(groups.entries());
  }, [filteredLocations]);

  const groupedByEvent = useMemo(() => {
    const groups = new Map<string, GuestVisibleLocation[]>();

    for (const location of filteredLocations) {
      const links = eventLinks.filter(
        (item) => item.location_id === location.id,
      );
      if (links.length === 0) {
        const current = groups.get('Occasion-wide') ?? [];
        current.push(location);
        groups.set('Occasion-wide', current);
        continue;
      }

      for (const link of links) {
        const current = groups.get(link.event_id) ?? [];
        current.push(location);
        groups.set(link.event_id, current);
      }
    }

    return Array.from(groups.entries());
  }, [eventLinks, filteredLocations]);

  const selectedLocationEventIds = useMemo(
    () =>
      selectedLocation
        ? eventLinks
            .filter((item) => item.location_id === selectedLocation.id)
            .map((item) => item.event_id)
        : [],
    [eventLinks, selectedLocation],
  );

  const selectedLocationLinks = useMemo(
    () =>
      selectedLocation
        ? locationLinks.filter(
            (item) => item.location_id === selectedLocation.id,
          )
        : [],
    [locationLinks, selectedLocation],
  );
  const selectedLocationContacts = useMemo(
    () =>
      selectedLocation
        ? contacts.filter((item) => item.location_id === selectedLocation.id)
        : [],
    [contacts, selectedLocation],
  );

  const selectedLocationOverlays = useMemo(
    () =>
      selectedLocation
        ? overlays.filter((item) => item.location_id === selectedLocation.id)
        : [],
    [overlays, selectedLocation],
  );
  const publishingTravelPacket = useMemo(() => {
    const lines = [
      `${
        access.status === 'ready'
          ? access.experience.occasion.displayName
          : msg('general.fields.occasion.label')
      } ${msg('pages.travel_packet_page.travel_packet')}`,
      '',
      ...filteredLocations.map((location) => {
        const locationContacts = contacts.filter(
          (contact) => contact.location_id === location.id,
        );
        const locationSupportLinks = locationLinks.filter(
          (link) => link.location_id === location.id,
        );

        return [
          location.title,
          `${msg('pages.travel_packet_page.type')}: ${formatEnumLabel(
            location.type,
          )}`,
          `${msg('pages.travel_packet_page.address')}: ${
            location.addressText ??
            msg('pages.travel_packet_page.address_pending')
          }`,
          locationContacts.length > 0
            ? `${msg('pages.travel_packet_page.contacts')}: ${locationContacts
                .map((contact) => `${contact.label} (${contact.value})`)
                .join(', ')}`
            : msg('pages.locations_page.contacts_none_published'),
          locationSupportLinks.length > 0
            ? `${msg('pages.travel_packet_page.links')}: ${locationSupportLinks
                .map((link) => `${link.label} (${link.url})`)
                .join(', ')}`
            : msg('pages.locations_page.links_none_published'),
        ].join('\n');
      }),
    ];

    return lines.join('\n\n');
  }, [access, contacts, filteredLocations, locationLinks]);
  const travelGuidanceSummary = useMemo(
    () => ({
      mapped: mapMarkers.length,
      parking: locations.filter((location) => location.type === 'parking')
        .length,
      shuttle: locations.filter(
        (location) => location.type === 'transportation_point',
      ).length,
      stays: locations.filter((location) => location.type === 'accommodation')
        .length,
      contactPoints: contacts.length,
    }),
    [contacts.length, locations, mapMarkers.length],
  );

  function copyTravelText(contents: string) {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(contents);
      notifications.success(
        msg('pages.locations_page.pages_locations_page_travel_details_copied'),
      );
      return;
    }

    notifications.error(
      msg('pages.locations_page.pages_locations_page_copy_unavailable'),
    );
  }

  if (access.status !== 'ready' || locationsQuery.isLoading) {
    return (
      <TovCard variant="content">
        <p className="page-eyebrow">{msg('general.modules.locations.name')}</p>
        <h2>
          {msg(
            'pages.locations_page.pages_locations_page_loading_your_location_view',
          )}
        </h2>
      </TovCard>
    );
  }

  if (locationsQuery.isError) {
    return (
      <TovCard variant="content">
        <p className="page-eyebrow">{msg('general.modules.locations.name')}</p>
        <h2>
          {msg(
            'pages.locations_page.pages_locations_page_locations_unavailable',
          )}
        </h2>
        <p>
          {getGuestErrorMessage(locationsQuery.error, locationsErrorMessage)}
        </p>
      </TovCard>
    );
  }

  return (
    <div className="page-grid">
      <TovCard variant="content" isWide>
        <p className="page-eyebrow">{msg('general.modules.locations.name')}</p>
        <h2>
          {msg(
            'pages.locations_page.pages_locations_page_places_shared_with_your_invitation',
          )}
        </h2>
        <p>
          {msg(
            'pages.locations_page.pages_locations_page_this_view_is_personalized_by_your_invitation_access',
          )}{' '}
        </p>
        <CachedDataNotice cachedValue={locationsResponse} />
        <div className="hero-card__stats">
          <article>
            <strong>{filteredLocations.length}</strong>
            <span>
              {msg(
                'pages.locations_page.pages_locations_page_visible_locations',
              )}
            </span>
          </article>
          <article>
            <strong>{travelGuidanceSummary.mapped}</strong>
            <span>
              {msg('pages.locations_page.pages_locations_page_mapped_stops')}
            </span>
          </article>
          <article>
            <strong>
              {travelGuidanceSummary.parking + travelGuidanceSummary.shuttle}
            </strong>
            <span>
              {msg('pages.locations_page.pages_locations_page_arrival_aids')}
            </span>
          </article>
          <article>
            <strong>{travelGuidanceSummary.stays}</strong>
            <span>
              {msg('pages.locations_page.pages_locations_page_stay_locations')}
            </span>
          </article>
          <article>
            <strong>{travelGuidanceSummary.contactPoints}</strong>
            <span>
              {msg(
                'pages.locations_page.pages_locations_page_travel_contact_point',
              )}
            </span>
          </article>
        </div>
        <div className="action-row">
          <TovButton
            variant="secondary"
            onClick={() => copyTravelText(publishingTravelPacket)}
          >
            {msg(
              'pages.locations_page.pages_locations_page_copy_travel_packet',
            )}{' '}
          </TovButton>
          <TovButton
            variant="secondary"
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.print();
              }
            }}
          >
            {msg(
              'pages.locations_page.pages_locations_page_print_day_of_packet',
            )}{' '}
          </TovButton>
          <TovButton asChild variant="secondary">
            <a
              href={`mailto:?subject=${encodeURIComponent('Travel packet')}&body=${encodeURIComponent(publishingTravelPacket)}`}
            >
              {msg(
                'pages.locations_page.pages_locations_page_email_packet',
              )}{' '}
            </a>
          </TovButton>
          <TovButton asChild variant="secondary">
            <NavLink to="/travel-packet">
              {msg(
                'pages.locations_page.pages_locations_page_open_travel_packet',
              )}
            </NavLink>
          </TovButton>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={msg('general.fields.search.placeholder')}
          />
          <Select
            value={eventFilter}
            onChange={(event) => setEventFilter(event.target.value)}
          >
            <SelectOption value="">
              {msg('pages.locations_page.pages_locations_page_all_events')}
            </SelectOption>
            {eventIds.map((eventId) => (
              <SelectOption key={eventId} value={eventId}>
                {eventId}
              </SelectOption>
            ))}
          </Select>
          <TovButton
            variant={viewMode === 'list' ? 'default' : 'secondary'}
            onClick={() => setViewMode('list')}
          >
            {msg('pages.locations_page.pages_locations_page_list')}{' '}
          </TovButton>
          <TovButton
            variant={viewMode === 'type' ? 'default' : 'secondary'}
            onClick={() => setViewMode('type')}
          >
            {msg('pages.locations_page.pages_locations_page_by_type')}{' '}
          </TovButton>
          <TovButton
            variant={viewMode === 'event' ? 'default' : 'secondary'}
            onClick={() => setViewMode('event')}
          >
            {msg('pages.locations_page.pages_locations_page_by_event')}{' '}
          </TovButton>
          <TovButton
            variant={viewMode === 'map' ? 'default' : 'secondary'}
            onClick={() => setViewMode('map')}
          >
            {msg('pages.locations_page.pages_locations_page_map')}{' '}
          </TovButton>
          <TovButton
            variant="secondary"
            onClick={() => void locationsQuery.refetch()}
          >
            {msg('general.actions.refresh')}{' '}
          </TovButton>
        </div>
      </TovCard>

      <TovCard variant="content" isWide>
        <p className="page-eyebrow">
          {msg('pages.locations_page.pages_locations_page_travel_playbook')}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg(
                'pages.locations_page.pages_locations_page_event_linked_guidance',
              )}
            </strong>
            <span>
              {msg(
                'pages.locations_page.pages_locations_page_locations_are_grouped_back_to_the_events_in',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.locations_page.pages_locations_page_venue_map_support',
              )}
            </strong>
            <span>
              {msg(
                'pages.locations_page.pages_locations_page_open_a_location_detail_to_review_map_markers',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.locations_page.pages_locations_page_parking_and_shuttle_clarity',
              )}
            </strong>
            <span>
              {msg(
                'pages.locations_page.pages_locations_page_parking_areas_and_transportation_points_are_surfaced_separately',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.locations_page.pages_locations_page_host_contacts_and_operators',
              )}
            </strong>
            <span>
              {msg(
                'pages.locations_page.pages_locations_page_when_hosts_shared_venue_hotel_transport_or_day',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.locations_page.pages_locations_page_need_another_route',
              )}
            </strong>
            <span>
              {msg(
                'pages.locations_page.pages_locations_page_use_schedule_for_timing_events_for_context_and',
              )}{' '}
            </span>
          </article>
        </div>
      </TovCard>

      <TovCard variant="content">
        <p className="page-eyebrow">
          {msg('pages.today_page.pages_today_page_travel_packet')}
        </p>
        <p>
          {msg(
            'pages.locations_page.pages_locations_page_copy_print_or_email_the_visible_location_set',
          )}{' '}
        </p>
        <div className="detail-list">
          <article>
            <strong>
              {msg('pages.locations_page.pages_locations_page_publishing_set')}
            </strong>
            <span>
              {filteredLocations.length}{' '}
              {msg('pages.locations_page.pages_locations_page_visible_stop')}{' '}
              {filteredLocations.length === 1 ? '' : 's'}{' '}
              {msg(
                'pages.locations_page.pages_locations_page_can_be_exported_from_this_page_for_day',
              )}{' '}
            </span>
          </article>
          <article>
            <strong>
              {msg(
                'pages.locations_page.pages_locations_page_contact_coverage',
              )}
            </strong>
            <span>
              {contacts.length > 0
                ? `${contacts.length} ${
                    contacts.length === 1
                      ? msg('pages.locations_page.host_provided_contact_point')
                      : msg('pages.locations_page.host_provided_contact_points')
                  } ${msg('pages.locations_page.are_attached_to_the_visible_travel_set')}`
                : msg(
                    'pages.locations_page.pages_locations_page_no_explicit_host_provided_travel_contacts_have_been_published',
                  )}
            </span>
          </article>
        </div>
      </TovCard>

      {viewMode === 'list' ? (
        <TovCard variant="content" isWide>
          <div className="detail-list">
            {filteredLocations.map((location) => (
              <article
                key={location.id}
                onClick={() => setSelectedLocationId(location.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setSelectedLocationId(location.id);
                  }
                }}
                role="button"
                tabIndex={0}
                className="guest-location-row"
              >
                <strong>{location.title}</strong>
                <span>
                  {getLocationGuidanceLabel(location.type)} •{' '}
                  {location.addressText ??
                    msg('general.states.address_pending')}
                </span>
                <span>
                  {eventLinks
                    .filter((item) => item.location_id === location.id)
                    .map(
                      (item) =>
                        eventNameById.get(item.event_id) ?? item.event_id,
                    )
                    .join(', ') ||
                    msg(
                      'pages.locations_page.pages_locations_page_occasion_wide_guidance',
                    )}
                </span>
                <span>
                  {location.latitude !== null && location.longitude !== null
                    ? msg(
                        'pages.locations_page.pages_locations_page_coordinates_available',
                      )
                    : msg(
                        'pages.locations_page.pages_locations_page_list_only_location',
                      )}
                </span>
              </article>
            ))}
          </div>
        </TovCard>
      ) : null}

      {viewMode === 'type' ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg('pages.locations_page.pages_locations_page_grouped_by_type')}
          </p>
          {groupedByType.map(([type, items]) => (
            <article key={type} className="guest-location-group">
              <h3>{formatEnumLabel(type)}</h3>
              <p className="tov-table__meta">
                {items.map((item) => item.title).join(', ')}
              </p>
            </article>
          ))}
        </TovCard>
      ) : null}

      {viewMode === 'event' ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg('pages.locations_page.pages_locations_page_grouped_by_event')}
          </p>
          {groupedByEvent.map(([eventId, items]) => (
            <article key={eventId} className="guest-location-group">
              <h3>{eventNameById.get(eventId) ?? eventId}</h3>
              <p className="tov-table__meta">
                {items.map((item) => item.title).join(', ')}
              </p>
            </article>
          ))}
        </TovCard>
      ) : null}

      {viewMode === 'map' ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg('pages.locations_page.pages_locations_page_map_markers')}
          </p>
          <div className="detail-list">
            {mapMarkers.length === 0 ? (
              <p className="tov-table__meta">
                {msg(
                  'pages.locations_page.pages_locations_page_no_mapped_locations_available',
                )}
              </p>
            ) : (
              mapMarkers.map((marker) => (
                <article key={marker.location_id}>
                  <strong>{marker.title}</strong>
                  <span>{formatEnumLabel(marker.type)}</span>
                  <span>
                    {marker.latitude}, {marker.longitude}
                  </span>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(`${marker.latitude},${marker.longitude}`)}`}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {msg(
                      'pages.locations_page.pages_locations_page_open_directions',
                    )}{' '}
                  </a>
                </article>
              ))
            )}
          </div>
        </TovCard>
      ) : null}

      {selectedLocation ? (
        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg('pages.locations_page.pages_locations_page_location_detail')}
          </p>
          <h3>{selectedLocation.title}</h3>
          <p>
            {selectedLocation.description ??
              msg(
                'pages.locations_page.pages_locations_page_details_will_be_shared_soon',
              )}
          </p>
          <p className="tov-table__meta">
            {getLocationGuidanceLabel(selectedLocation.type)}
          </p>
          <div className="action-row">
            <Badge variant="outline">
              {formatEnumLabel(selectedLocation.type)}
            </Badge>
            {selectedLocation.mapLabel ? (
              <Badge variant="outline">{selectedLocation.mapLabel}</Badge>
            ) : null}
          </div>
          <p className="tov-table__meta">
            {msg('pages.locations_page.pages_locations_page_event_relevance')}{' '}
            {selectedLocationEventIds.length > 0
              ? selectedLocationEventIds
                  .map((eventId) => eventNameById.get(eventId) ?? eventId)
                  .join(', ')
              : msg('pages.gallery_page.pages_gallery_page_occasion_wide')}
          </p>
          <div className="guest-location-context">
            {selectedLocationEventIds.length > 0 ? (
              selectedLocationEventIds.map((eventId) => (
                <NavLink key={eventId} to={`/events/${eventId}`}>
                  <TovIcon name="calendar-blank" />
                  {eventNameById.get(eventId) ?? eventId}
                </NavLink>
              ))
            ) : (
              <span>
                <TovIcon name="globe" />
                {msg('pages.gallery_page.pages_gallery_page_occasion_wide')}
              </span>
            )}
            {selectedLocationContacts.length > 0 ? (
              <NavLink to="/help">
                <TovIcon name="lifebuoy" />
                {selectedLocationContacts.length}{' '}
                {msg('pages.locations_page.pages_locations_page_contact_point')}
              </NavLink>
            ) : null}
            {selectedLocationLinks.length > 0 ? (
              <span>
                <TovIcon name="link" />
                {selectedLocationLinks.length}{' '}
                {msg('pages.locations_page.pages_locations_page_support_link')}
              </span>
            ) : null}
          </div>
          <p className="tov-table__meta">
            {msg('general.fields.address.label')}{' '}
            {selectedLocation.addressText ??
              msg('general.states.address_pending')}
          </p>
          {selectedLocation.latitude !== null &&
          selectedLocation.longitude !== null ? (
            <div className="action-row">
              <a
                className="secondary-link"
                href={`https://maps.google.com/?q=${encodeURIComponent(`${selectedLocation.latitude},${selectedLocation.longitude}`)}`}
                rel="noreferrer"
                target="_blank"
              >
                {msg(
                  'pages.locations_page.pages_locations_page_open_turn_by_turn_directions',
                )}{' '}
              </a>
              <a
                className="secondary-link"
                href={`https://maps.apple.com/?ll=${selectedLocation.latitude},${selectedLocation.longitude}`}
                rel="noreferrer"
                target="_blank"
              >
                {msg(
                  'pages.locations_page.pages_locations_page_open_in_apple_maps',
                )}{' '}
              </a>
            </div>
          ) : null}
          <div className="detail-list">
            <article>
              <strong>
                {msg(
                  'pages.locations_page.pages_locations_page_arrival_guidance',
                )}
              </strong>
              <span>
                {selectedLocation.type === 'parking'
                  ? msg(
                      'pages.locations_page.pages_locations_page_use_this_stop_for_parking_access_before_walking_or',
                    )
                  : selectedLocation.type === 'transportation_point'
                    ? msg(
                        'pages.locations_page.pages_locations_page_use_this_stop_for_pickup_shuttle_or_transfer_coordination',
                      )
                    : selectedLocation.type === 'accommodation'
                      ? msg(
                          'pages.locations_page.pages_locations_page_use_this_location_for_stay_planning_check_in_and',
                        )
                      : msg(
                          'pages.locations_page.pages_locations_page_use_this_location_as_the_main_guest_facing_waypoint',
                        )}
              </span>
            </article>
            <article>
              <strong>
                {msg(
                  'pages.locations_page.pages_locations_page_transport_support',
                )}
              </strong>
              <span>
                {selectedLocation.type === 'transportation_point'
                  ? msg(
                      'pages.locations_page.pages_locations_page_use_this_stop_for_shared_pickup_timing_transfer_coordination',
                    )
                  : selectedLocation.type === 'parking'
                    ? msg(
                        'pages.locations_page.pages_locations_page_use_parking_details_first_then_open_linked_map_or',
                      )
                    : selectedLocation.type === 'accommodation'
                      ? msg(
                          'pages.locations_page.pages_locations_page_check_for_hotel_room_block_or_concierge_links_before',
                        )
                      : msg(
                          'pages.locations_page.pages_locations_page_open_the_linked_maps_support_links_and_contacts_below',
                        )}
              </span>
            </article>
            <article>
              <strong>
                {msg(
                  'pages.locations_page.pages_locations_page_linked_surfaces',
                )}
              </strong>
              <span>
                {msg(
                  'pages.locations_page.pages_locations_page_review_schedule_for_timing_changes_and_events_for',
                )}{' '}
              </span>
            </article>
          </div>
          {selectedLocationLinks.length > 0 ? (
            <div className="detail-list">
              {selectedLocationLinks.map((link) => (
                <article key={`${link.link_type}:${link.url}`}>
                  <strong>{link.label}</strong>
                  <span>{formatEnumLabel(link.link_type)}</span>
                  <a href={link.url} rel="noreferrer" target="_blank">
                    {msg(
                      'pages.locations_page.pages_locations_page_open_support_link',
                    )}{' '}
                  </a>
                </article>
              ))}
            </div>
          ) : null}
          {selectedLocationContacts.length > 0 ? (
            <>
              <h4>
                {msg(
                  'pages.locations_page.pages_locations_page_who_to_contact',
                )}
              </h4>
              <div className="detail-list">
                {selectedLocationContacts.map((contact) => (
                  <article key={contact.id}>
                    <strong>{contact.label}</strong>
                    <span>{getContactTypeLabel(contact.contact_type)}</span>
                    <span>{contact.value}</span>
                    <span>
                      {contact.notes ??
                        msg(
                          'pages.locations_page.pages_locations_page_use_this_contact_if_you_need_help_with_arrival',
                        )}
                    </span>
                  </article>
                ))}
              </div>
            </>
          ) : null}
          {selectedLocationOverlays.length > 0 || venueOverlays.length > 0 ? (
            <>
              <h4>
                {msg(
                  'pages.locations_page.pages_locations_page_venue_map_points',
                )}
              </h4>
              <div className="detail-list">
                {[...selectedLocationOverlays, ...venueOverlays].map(
                  (overlay) => (
                    <article key={overlay.id}>
                      <strong>{overlay.label}</strong>
                      <span>
                        {msg('pages.locations_page.pages_locations_page_x')}{' '}
                        {overlay.x}
                        {msg(
                          'pages.locations_page.pages_locations_page_y',
                        )}{' '}
                        {overlay.y}
                      </span>
                    </article>
                  ),
                )}
              </div>
            </>
          ) : (
            <p className="tov-table__meta">
              {msg(
                'pages.locations_page.pages_locations_page_no_venue_map_points_available_for_this_location',
              )}{' '}
            </p>
          )}
          <div className="action-row">
            <TovButton asChild variant="secondary">
              <NavLink to="/events">
                {msg('pages.locations_page.pages_locations_page_event_context')}
              </NavLink>
            </TovButton>
            <TovButton asChild variant="secondary">
              <NavLink to="/schedule">
                {msg(
                  'pages.locations_page.pages_locations_page_timing_and_schedule',
                )}
              </NavLink>
            </TovButton>
            <TovButton
              variant="secondary"
              onClick={() => {
                const locationContacts = selectedLocationContacts
                  .map((contact) => `${contact.label}: ${contact.value}`)
                  .join('\n');
                const locationSummary = [
                  selectedLocation.title,
                  selectedLocation.addressText ??
                    msg('general.states.address_pending'),
                  locationContacts ||
                    msg(
                      'pages.locations_page.pages_locations_page_no_explicit_contact_published',
                    ),
                ].join('\n');

                copyTravelText(locationSummary);
              }}
            >
              {msg(
                'pages.locations_page.pages_locations_page_copy_details',
              )}{' '}
            </TovButton>
            <TovButton asChild variant="secondary">
              <NavLink to="/help">
                {msg('pages.messages_page.pages_messages_page_get_help')}
              </NavLink>
            </TovButton>
          </div>
        </TovCard>
      ) : null}
    </div>
  );
}
